import { useQuery } from '@tanstack/react-query';

import type { MovieDto } from '@cineamo/api/models/movie/v2/MovieDto.types';
import type { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import type { UrlQueryParameter } from '@cineamo/api/helpers/url-query-helper';
import { getShowings } from '@cineamo/api/resources/showing/showing-api';

import { formatDate_yyyyMMddTHHmmssZ, logicalStartOfToday } from '@cineamo/ui/src/helpers/date-helper';

/**
 * `startDatetime` of the movie's next showing from today on, or `undefined` when the request is
 * still in flight, failed, or the movie has no upcoming showing at all.
 *
 * One row ordered by `startDatetime` — the column the list query already sorts and indexes on — so
 * this is cheaper than any of the per-day count queries the date strip fires anyway.
 *
 * Deliberately not location-scoped: `/showings` only narrows by location when a `distance` is
 * given, and the finder never sends one (a radius must not hide cinemas playing the movie), so the
 * answer is the same for every visitor and a single cache entry serves them all.
 */
export function useEarliestShowingDate(
    movieId: MovieDto['id'],
    apiFilters?: UrlQueryParameter,
    options?: { enabled?: boolean }
): { earliestShowingStartDatetime?: ShowingDto['startDatetime']; isLoading: boolean } {
    // `startTime`/`endTime` are the time-of-day filters, narrowed client-side once the showings are
    // in hand; they are not API params, and the day-count queries strip them the same way.
    const serverFilters: UrlQueryParameter = Object.fromEntries(
        Object.entries(apiFilters ?? {}).filter(([key]) => key !== 'startTime' && key !== 'endTime')
    );
    const startDatetime = formatDate_yyyyMMddTHHmmssZ(logicalStartOfToday());

    const { data, isLoading } = useQuery({
        queryKey: ['earliest-showing', `movieId-${movieId}`, `from-${startDatetime}`, JSON.stringify(serverFilters)],
        queryFn: () =>
            getShowings({
                movieId,
                startDatetime,
                sort: 'startDatetime',
                order: 'asc',
                perPage: 1,
                ...serverFilters
            }),
        enabled: options?.enabled !== false,
        staleTime: 5000
    });

    return {
        earliestShowingStartDatetime: data?._embedded?.showings?.[0]?.startDatetime,
        isLoading
    };
}
